import { css, cx } from '@mildang/styled-system/css';
import { splitCssProps } from '@mildang/styled-system/jsx';
import Text from '../../Text';
import { HELP_TEXT_ICONS, HelpTextVariantLiteral } from './constants';
import { Slot } from 'radix-ui';
import { helpText } from '@mildang/styled-system/recipes';
import type { PandaHTMLStyledProps } from '../../../types/HTMLStyledProps';
export interface BaseHelpTextProps {
  /** 메시지의 의미와 아이콘·색상. 기본값은 `info`. */
  variant?: HelpTextVariantLiteral;
  /** variant에 대응하는 상태 아이콘 표시 여부. 기본값은 `false`. */
  showIcon?: boolean;
  /** 자식 요소에 HelpText의 스타일과 접근성 속성을 합성함. 링크나 `p` 등 다른 요소로 렌더할 때 사용 (`as`는 미지원). */
  asChild?: boolean;
  /** 최상위 엘리먼트에 붙는 className. */
  className?: string;
}

// 루트는 항상 span 이다. 다른 엘리먼트로 렌더하려면 `as` 대신 `asChild` 를 쓴다.
export type HelpTextProps = BaseHelpTextProps & Omit<PandaHTMLStyledProps<'span'>, 'as'>;

export const HelpText = (props: HelpTextProps) => {
  const { variant = 'info', showIcon = false, children, asChild = false, className, sx, ...others } = props;
  // style props 는 className 으로 바꿔 넘긴다 — asChild 일 때 Slot 은 panda style props 를 처리하지 않아
  // 그대로 넘기면 DOM 속성으로 새고, `translate` 처럼 HTML 속성과 이름이 겹치면 타입도 충돌한다.
  const [cssProps, domProps] = splitCssProps(others);
  const { css: _cssStyleProps, ...styleProps } = cssProps;

  const IconComponent = HELP_TEXT_ICONS[variant as HelpTextVariantLiteral];
  const classes = helpText({ variant });

  const getIconElement = () => {
    if (!showIcon) return null;
    return <IconComponent className={classes.icon} />;
  };

  const iconElement = getIconElement();
  const rootClassName = cx(classes.root, css(styleProps, sx), className);

  // Slot.Root | Text union 으로 한 번에 렌더하면 Text 의 `as` 추론이 풀려 ref 타입이 어긋난다.
  // Slottable 은 Slot.Root 의 직계 자식이어야 해서 Fragment 로 묶지 않고 분기마다 적는다.
  if (asChild) {
    return (
      <Slot.Root className={rootClassName} {...domProps}>
        {variant !== 'link' && iconElement}
        <Slot.Slottable>{children}</Slot.Slottable>
        {variant === 'link' && iconElement}
      </Slot.Root>
    );
  }

  return (
    <Text as="span" variant="caption-md-medium" className={rootClassName} {...domProps}>
      {variant !== 'link' && iconElement}
      {children}
      {variant === 'link' && iconElement}
    </Text>
  );
};

export default HelpText;
